import { useEffect } from 'react';
import Lenis from 'lenis';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import { LanguageProvider } from '@/i18n';
import { Navbar } from '@/components/Navbar';
import { Hero } from '@/components/Hero';
import { HowItWorks } from '@/components/HowItWorks';
import { LocalNetwork } from '@/components/LocalNetwork';
import { NetworkSection } from '@/components/NetworkSection';
import { FeaturedPackages } from '@/components/FeaturedPackages';
import { FeaturedStays, FinalCta, GuidesBand, InspirationSection, OurStoryBand, TravelPartnersBand } from '@/components/HomeSections';
import { PlanTripBand } from '@/components/PlanTripBand';
import { MapSection } from '@/components/MapSection';
import { Featured } from '@/components/Featured';
import { Partners } from '@/components/Partners';
import { Footer } from '@/components/Footer';
import { CookieConsent } from '@/components/CookieConsent';
import CompanyInfoPage from '@/pages/CompanyInfoPage';
import LegalPage from '@/pages/LegalPage';
import Admin from '@/components/Admin';
import DestinationsPage from '@/pages/DestinationsPage';
import DestinationDetailPage from '@/pages/DestinationDetailPage';
import ExperiencesPage from '@/pages/ExperiencesPage';
import ExperienceDetailPage from '@/pages/ExperienceDetailPage';
import PackagesPage from '@/pages/PackagesPage';
import PackageDetailPage from '@/pages/PackageDetailPage';
import RequestProposalPage from '@/pages/RequestProposalPage';
import AccommodationsPage from '@/pages/AccommodationsPage';
import TransfersPage from '@/pages/TransfersPage';
import RentACarPage from '@/pages/RentACarPage';
import RentACarDetailPage from '@/pages/RentACarDetailPage';
import TravelGuidesPage from '@/pages/TravelGuidesPage';
import TravelGuideArticlePage from '@/pages/TravelGuideArticlePage';
import PartnersPage from '@/pages/PartnersPage';
import PartnerDetailPage from '@/pages/PartnerDetailPage';
import PartnerDashboard from '@/pages/PartnerDashboard';
import PartnerProductDetailPage from '@/pages/PartnerProductDetailPage';
import JoinPage from '@/pages/JoinPage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import AccommodationDetailPage from '@/pages/AccommodationDetailPage';
import PlanMyTripPage from '@/pages/PlanMyTripPage';
import ToursPage from '@/pages/ToursPage';
import MyTripPage from '@/pages/MyTripPage';
import ProposalPage from '@/pages/ProposalPage';
import LoginPage from '@/pages/LoginPage';
import VoucherPage from '@/pages/VoucherPage';
import CruisePage from '@/pages/CruisePage';
import CruiseDetailPage from '@/pages/CruiseDetailPage';
import TravelTradePage from '@/pages/TravelTradePage';
import RequestStayPage from '@/pages/RequestStayPage';
import RequestExperiencePage from '@/pages/RequestExperiencePage';
import TravellerAccessPage from '@/pages/TravellerAccessPage';
import FlightsPage from '@/pages/FlightsPage';
import { SiteContentProvider } from '@/utils/siteContent';
import { ExternalPartnerSection } from '@/components/ExternalPartnerSection';
import { GetYourGuideAnalytics } from '@/components/GetYourGuideAnalytics';
import { EcoAssist } from '@/components/EcoAssist';
import { CuratedPaths } from '@/components/CuratedPaths';
import { FavoritesProvider } from '@/utils/favorites';
import { CurrencyProvider } from '@/utils/currency';
import { CartProvider } from '@/utils/cart';
import { EcoMoments } from '@/components/EcoMoments';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    // Mobile menus and lazy chunks shift layout right after navigation — retry until the target is scrollable
    let attempts = 0;
    let timer;
    const tryScroll = () => {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < 20) timer = setTimeout(tryScroll, 150);
    };
    timer = setTimeout(tryScroll, 350);
    return () => clearTimeout(timer);
  }, [pathname, hash]);
  return null;
};

const Landing = () => (
  <main>
    <Hero />
    <NetworkSection />
    <CuratedPaths />
    <EcoMoments />
    <InspirationSection />
    <Featured />
    <FeaturedStays />
    <FeaturedPackages />
    <ExternalPartnerSection />
    <HowItWorks />
    <PlanTripBand />
    <LocalNetwork />
    <Partners />
    <GuidesBand />
    <TravelPartnersBand />
    <OurStoryBand />
    <div id="mapa" className="scroll-mt-10">
      <MapSection />
    </div>
    <FinalCta />
  </main>
);

const JourneyRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={slug ? `/journeys/${slug}` : '/journeys'} replace />;
};

const Shell = () => {
  const { pathname } = useLocation();
  const bare = pathname.startsWith('/admin');

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    const onLock = () => lenis.stop();
    const onUnlock = () => lenis.start();
    window.addEventListener('eco:lenis-stop', onLock);
    window.addEventListener('eco:lenis-start', onUnlock);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.removeEventListener('eco:lenis-stop', onLock);
      window.removeEventListener('eco:lenis-start', onUnlock);
    };
  }, []);

  return (
    <div className="min-h-screen bg-eco-cream font-sans text-eco-ink antialiased">
      <ScrollToTop />
      {!bare && <GetYourGuideAnalytics />}
      {!bare && <Navbar />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/destinations" element={<DestinationsPage />} />
        <Route path="/destinations/:slug" element={<DestinationDetailPage />} />
        <Route path="/destinations/:slug/:islandSlug" element={<DestinationDetailPage />} />
        <Route path="/experiences" element={<ExperiencesPage />} />
        <Route path="/experiences/:slug" element={<ExperienceDetailPage />} />
        <Route path="/full-packages" element={<PackagesPage />} />
        <Route path="/full-packages/:slug" element={<PackageDetailPage />} />
        <Route path="/request-proposal" element={<RequestProposalPage />} />
        <Route path="/request-experience" element={<RequestExperiencePage />} />
        <Route path="/tours" element={<JourneyRedirect />} />
        <Route path="/tours/:slug" element={<JourneyRedirect />} />
        <Route path="/journeys" element={<ToursPage />} />
        <Route path="/journeys/:slug" element={<PackageDetailPage />} />
        <Route path="/cruise" element={<CruisePage />} />
        <Route path="/cruise/:slug" element={<CruiseDetailPage />} />
        <Route path="/travel-trade" element={<TravelTradePage />} />
        <Route path="/accommodations" element={<AccommodationsPage />} />
        <Route path="/stay-and-discover" element={<AccommodationsPage stayDiscover />} />
        <Route path="/accommodations/:slug" element={<AccommodationDetailPage />} />
        <Route path="/request-stay" element={<RequestStayPage />} />
        <Route path="/transfers" element={<TransfersPage />} />
        <Route path="/flights" element={<FlightsPage />} />
        <Route path="/rent-a-car" element={<RentACarPage />} />
        <Route path="/rent-a-car/:id" element={<RentACarDetailPage />} />
        <Route path="/travel-guides" element={<TravelGuidesPage />} />
        <Route path="/travel-guides/:slug" element={<TravelGuideArticlePage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partners/:slug" element={<PartnerDetailPage />} />
        <Route path="/partner-products/:id" element={<PartnerProductDetailPage />} />
        <Route path="/partner" element={<PartnerDashboard />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/company-information" element={<CompanyInfoPage />} />
        <Route path="/terms" element={<LegalPage docType="terms" titleKey="foot.leg.terms" testid="legal-terms" />} />
        <Route path="/privacy" element={<LegalPage docType="privacy" titleKey="foot.leg.privacy" testid="legal-privacy" />} />
        <Route path="/cookies" element={<LegalPage docType="cookies" titleKey="foot.leg.cookies" testid="legal-cookies" />} />
        <Route path="/cancellation-policy" element={<LegalPage docType="cancellation" titleKey="foot.leg.cancellation" testid="legal-cancellation" />} />
        <Route path="/affiliate-disclosure" element={<LegalPage docType="affiliate_disclosure" titleKey="foot.leg.affiliate" testid="legal-affiliate" />} />
        <Route path="/partner-terms" element={<LegalPage docType="partner_terms" titleKey="foot.leg.partner" testid="legal-partner" />} />
        <Route path="/plan-my-trip" element={<PlanMyTripPage />} />
        <Route path="/build-your-journey" element={<PlanMyTripPage />} />
        <Route path="/my-trip" element={<MyTripPage />} />
        <Route path="/my-trip/access" element={<TravellerAccessPage />} />
        <Route path="/my-trip/proposals/:id" element={<ProposalPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/vouchers/:code" element={<VoucherPage />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!bare && <Footer />}
      {!bare && <EcoAssist />}
      {!bare && <CookieConsent />}
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <SiteContentProvider>
        <CurrencyProvider><CartProvider><FavoritesProvider><BrowserRouter><Shell /></BrowserRouter></FavoritesProvider></CartProvider></CurrencyProvider>
      </SiteContentProvider>
      <Toaster position="bottom-left" toastOptions={{ style: { background: '#122e26', color: '#fff', border: '1px solid rgba(201,169,97,.35)' } }} />
    </LanguageProvider>
  );
}

export default App;
